import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";

// Pages
import Landing from "./pages/Landing";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import Submit from "./pages/Submit";
import Gallery from "./pages/Gallery";
import Admin from "./pages/Admin";
import Winners from "./pages/Winners";
import ChallengeHistory from "./pages/ChallengeHistory";
import Leaderboard from "./pages/Leaderboard";
import OwnerLogin from "./pages/OwnerLogin";
import ShareCard from "./pages/ShareCard";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Feedback from "./pages/Feedback";
import OBSOverlay from "./pages/OBSOverlay";
import OBSFullscreenCard from "./pages/OBSFullscreenCard";

// Loading component
const LoadingScreen = () => (
  <div className="min-h-screen bg-black flex items-center justify-center">
    <div className="animate-spin w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full"></div>
  </div>
);

// Protected Route Component - requires authentication
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  console.log('[ProtectedRoute] loading:', loading, 'isAuthenticated:', isAuthenticated);
  
  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/" replace />;
  
  return children;
};

// Admin Route Component - renders Admin page which handles its own auth display
// This allows Admin.js to show appropriate messages instead of redirecting
const AdminRoute = ({ children }) => {
  const { loading } = useAuth();
  
  console.log('[AdminRoute] loading:', loading);
  
  // Only show loading during initial auth check
  if (loading) return <LoadingScreen />;
  
  // Let Admin.js handle the rest (it shows appropriate messages for non-auth/non-admin)
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/gallery" element={<Gallery />} />
      <Route path="/winners" element={<Winners />} />
      <Route path="/history" element={<ChallengeHistory />} />
      <Route path="/leaderboard" element={<Leaderboard />} />
      <Route path="/owner" element={<OwnerLogin />} />
      <Route path="/owner-login" element={<Navigate to="/owner" replace />} />
      <Route path="/share/:shareId" element={<ShareCard />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/feedback" element={<Feedback />} />
      
      {/* OBS Browser Source Overlay Routes */}
      <Route path="/obs/challenge/:challengeId" element={<OBSOverlay />} />
      <Route path="/obs/challenge" element={<OBSOverlay />} />
      <Route path="/obs" element={<OBSOverlay />} />
      
      {/* OBS Fullscreen Card Routes (1920x1080) */}
      <Route path="/obs/fullscreen/:challengeId" element={<OBSFullscreenCard />} />
      <Route path="/obs/fullscreen" element={<OBSFullscreenCard />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/submit" element={
        <ProtectedRoute>
          <Submit />
        </ProtectedRoute>
      } />
      
      {/* Admin Route - Admin.js handles its own auth checks and messages */}
      <Route path="/admin" element={
        <AdminRoute>
          <Admin />
        </AdminRoute>
      } />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
